import { useEffect, useMemo, useState } from "react";
import type {
  WidgetEvent,
  WidgetEventCta,
  WidgetEventTicketType,
} from "../types";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../lib/image";
import {
  formatBadge,
  resolveAvailabilityBadge,
  type AvailabilityBadge,
} from "../state/availability-badge";
import { EventDetailPanel } from "./EventDetailPanel";

type GroupBy = "month" | "type";
interface EventGroup {
  key: string;
  label: string;
  events: WidgetEvent[];
}

// The distinct categories present across the events, in the order they first
// appear — drives the group-by-type option and the filter chips. Empty until
// the tenant assigns categories (and the config surfaces them), so the whole
// control bar stays hidden for tenants that don't use categories.
function collectCategories(events: WidgetEvent[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const e of events) {
    if (e.category_id && e.category_name && !seen.has(e.category_id)) {
      seen.set(e.category_id, e.category_name);
    }
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name }));
}

// Group the (optionally category-filtered) events either by calendar month or
// by category. Events are ordered soonest-first within each group; month groups
// run chronologically, type groups follow first-appearance with uncategorised
// events last.
function buildEventGroups(
  events: WidgetEvent[],
  groupBy: GroupBy,
  activeCategoryId: string | null,
): EventGroup[] {
  const filtered = activeCategoryId
    ? events.filter((e) => e.category_id === activeCategoryId)
    : events;
  const sorted = [...filtered].sort(
    (a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at),
  );

  const groups: EventGroup[] = [];
  const index = new Map<string, EventGroup>();
  for (const e of sorted) {
    let key: string;
    let label: string;
    if (groupBy === "type") {
      key = e.category_id ?? "__uncat__";
      label = e.category_name ?? "Other events";
    } else {
      const d = new Date(e.starts_at);
      key = `${d.getFullYear()}-${d.getMonth()}`;
      label = d.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
    }
    let g = index.get(key);
    if (!g) {
      g = { key, label, events: [] };
      index.set(key, g);
      groups.push(g);
    }
    g.events.push(e);
  }

  if (groupBy === "type") {
    // Keep first-appearance order, but always sink uncategorised to the end.
    groups.sort((a, b) =>
      a.key === "__uncat__" ? 1 : b.key === "__uncat__" ? -1 : 0,
    );
  }
  return groups;
}

// Renders the body of the virtual __events__ tab — a card list of all the
// events the widget should surface. Used when widget.show_events_tab is
// true. Filtering by widget.enabled_event_ids is handled upstream so this
// component just renders whatever it's given.
export function EventsPanel({
  events,
  onAddedToCart,
  initialSelectedId = null,
  onConsumeInitialSelection,
  onCta,
  availabilityBadge,
}: {
  events: WidgetEvent[];
  onAddedToCart: () => void;
  /** Deep-link: event id to open straight into detail on mount (one-shot). */
  initialSelectedId?: string | null;
  onConsumeInitialSelection?: () => void;
  /** Cross-service CTA navigation, forwarded to the event detail. */
  onCta?: (cta: WidgetEventCta) => void;
  /** Tenant control over the "{count} left" badge on card buttons. */
  availabilityBadge?: AvailabilityBadge;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSelectedId,
  );
  const [groupBy, setGroupBy] = useState<GroupBy>("month");
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const categories = useMemo(() => collectCategories(events), [events]);
  const groups = useMemo(
    () => buildEventGroups(events, groupBy, activeCategoryId),
    [events, groupBy, activeCategoryId],
  );
  // Apply the deep-link target once, then tell the parent to clear it so
  // returning to the list (or re-mounting the tab) doesn't snap back to it.
  useEffect(() => {
    if (initialSelectedId) {
      setSelectedId(initialSelectedId);
      onConsumeInitialSelection?.();
    }
    // Deliberately not depending on onConsumeInitialSelection — it changes
    // every render in the parent, which would refire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedId]);
  const selected = selectedId
    ? events.find((e) => e.id === selectedId) ?? null
    : null;

  if (selected) {
    return (
      <EventDetailPanel
        event={selected}
        onBack={() => setSelectedId(null)}
        onAdded={onAddedToCart}
        onCta={onCta}
      />
    );
  }

  if (events.length === 0) {
    return (
      <div className="vt-events-empty">
        <strong>No upcoming events.</strong>
        <p>Check back soon — new events get posted regularly.</p>
      </div>
    );
  }

  // The control bar only appears once there are categories to switch/filter by;
  // without them the list simply groups by month (a nicer default than a flat
  // wall of cards). Visitors alternate month ⇄ type and filter to one category.
  const showControls = categories.length > 0;

  return (
    <div className="vt-events-panel">
      {showControls && (
        <div className="vt-events-controls">
          <div
            className="vt-events-groupby"
            role="tablist"
            aria-label="Group events by"
          >
            <button
              type="button"
              role="tab"
              aria-selected={groupBy === "month"}
              className={`vt-events-groupby-btn${groupBy === "month" ? " vt-events-groupby-active" : ""}`}
              onClick={() => setGroupBy("month")}
            >
              By month
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={groupBy === "type"}
              className={`vt-events-groupby-btn${groupBy === "type" ? " vt-events-groupby-active" : ""}`}
              onClick={() => setGroupBy("type")}
            >
              By type
            </button>
          </div>
          <div
            className="vt-events-filters"
            role="group"
            aria-label="Filter by category"
          >
            <button
              type="button"
              className={`vt-events-chip${activeCategoryId === null ? " vt-events-chip-active" : ""}`}
              onClick={() => setActiveCategoryId(null)}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`vt-events-chip${activeCategoryId === c.id ? " vt-events-chip-active" : ""}`}
                onClick={() => setActiveCategoryId(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="vt-events-empty">
          <strong>No events in this category.</strong>
          <p>Try another filter.</p>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="vt-events-group">
            <h3 className="vt-events-group-head">{g.label}</h3>
            <ul className="vt-events-list">
              {g.events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onSelect={() => setSelectedId(event.id)}
                  availabilityBadge={availabilityBadge}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function EventCard({
  event,
  onSelect,
  availabilityBadge,
}: {
  event: WidgetEvent;
  onSelect: () => void;
  availabilityBadge?: AvailabilityBadge;
}) {
  const dateRange = formatDateRange(event.starts_at, event.ends_at);
  // NaN-safe: the RPC can omit the capacity fields entirely (undefined, not
  // null), so only compute remaining/lowStock when BOTH are finite numbers.
  const isFiniteNum = (x: unknown): x is number =>
    typeof x === "number" && Number.isFinite(x);
  const remaining =
    isFiniteNum(event.total_capacity) && isFiniteNum(event.sold_count)
      ? Math.max(0, event.total_capacity - event.sold_count)
      : null;
  // Sold-out disabling is UNCONDITIONAL — hiding the badge wording must
  // never re-enable booking a full event.
  const soldOut = remaining !== null && remaining === 0;
  const lowStock =
    remaining !== null &&
    isFiniteNum(event.total_capacity) &&
    remaining > 0 &&
    remaining <= Math.max(5, Math.floor(event.total_capacity * 0.1));
  const badge = availabilityBadge ?? resolveAvailabilityBadge(null);

  // Prototype's `.event-card` is text-only: gold date-line caption on
  // top, Cormorant title, description paragraph, tier list separated by
  // a top border, gold-outline "Book from R X →" button at the bottom.
  // We mirror that structure (no hero image, no badge — kept the sold-out
  // dim for safety).
  const dateLine = [
    dateRange,
    event.venue_location,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li
      className={`vt-event-card${soldOut ? " vt-event-sold-out" : ""}${
        event.hero_image_url ? " vt-event-card-has-image" : ""
      }`}
    >
      {event.hero_image_url && (
        <div className="vt-event-card-hero">
          <img
            className="vt-cover-img"
            src={optimizedImageUrl(event.hero_image_url, { width: 800 })}
            srcSet={optimizedSrcSet(event.hero_image_url, IMG_WIDTHS.CARD)}
            sizes="(min-width: 900px) 33vw, (min-width: 600px) 50vw, 100vw"
            alt=""
            loading="lazy"
            decoding="async"
          />
          <span className="vt-event-card-hero-name">{event.name}</span>
        </div>
      )}
      <div className="vt-event-card-body">
      <div className="vt-event-date-line">{dateLine}</div>
      {!event.hero_image_url && <h3 className="vt-event-name">{event.name}</h3>}
      {event.summary && <p className="vt-event-summary">{event.summary}</p>}
      {event.ticket_types && event.ticket_types.length > 0 && (
        <TicketTierList types={event.ticket_types} />
      )}
      <button
        type="button"
        className="vt-event-card-button"
        onClick={onSelect}
        disabled={soldOut}
      >
        {soldOut ? (
          "Sold out"
        ) : lowStock && badge.shown ? (
          <>Book ({formatBadge(badge, remaining ?? 0)}) →</>
        ) : (
          <>
            <BookFromLabel
              cents={event.cheapest_price_cents}
              currency={event.currency}
            />
          </>
        )}
      </button>
      </div>
    </li>
  );
}

function BookFromLabel({
  cents,
  currency,
}: {
  cents: number | null;
  currency: string | null;
}) {
  if (cents === null || cents === 0) return <>Book free seat →</>;
  const symbol = currencySymbol(currency);
  return (
    <>
      Book from {symbol}
      {Math.round(cents / 100)} →
    </>
  );
}

// Tier list rendered inside the event card — matches the prototype's
// `.tiers` block: a top-bordered group of "name | price" rows.
function TicketTierList({ types }: { types: WidgetEventTicketType[] }) {
  const visible = types.slice(0, 4);
  const extra = types.length - visible.length;

  return (
    <div className="vt-event-tiers">
      {visible.map((t) => (
        <div key={t.id} className="vt-event-tier">
          <span className="vt-event-tier-name">{t.name}</span>
          <span className="vt-event-tier-price">
            {formatPrice(t.price_cents, t.currency)}
          </span>
        </div>
      ))}
      {extra > 0 && (
        <div className="vt-event-tier vt-event-tier-more">
          + {extra} more {extra === 1 ? "option" : "options"}
        </div>
      )}
    </div>
  );
}

function formatPrice(cents: number, currency: string): string {
  if (cents === 0) return "Free";
  return `${currencySymbol(currency)}${Math.round(cents / 100)}`;
}

function currencySymbol(code: string | null): string {
  switch (code) {
    case "ZAR":
      return "R ";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return code ? `${code} ` : "";
  }
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const datePart = start.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const timeFmt: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  if (sameDay) {
    return `${datePart} · ${start.toLocaleTimeString("en-ZA", timeFmt)} – ${end.toLocaleTimeString("en-ZA", timeFmt)}`;
  }
  const endDate = end.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return `${datePart} – ${endDate}`;
}
